import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'

import { usePublicPlans } from '@/features/billing/queries'

import { lightPalette } from '../palette'

export function PricingSection() {
  const { t, i18n } = useTranslation()
  const plans = usePublicPlans()
  // Preço vem do catálogo (nunca do código); nulo/indisponível = "em definição" (02 §8).
  const priceFor = (code: string): string => {
    const price = plans.data?.plans.find((p) => p.code === code)?.monthlyPrice ?? null
    return price === null
      ? t('landing.pricing.priceUnset')
      : new Intl.NumberFormat(i18n.language, {
          style: 'currency',
          currency: plans.data?.currency ?? 'BRL',
          maximumFractionDigits: 0,
        }).format(price)
  }
  const provisional =
    plans.data === undefined ||
    plans.data.plans.length === 0 ||
    plans.data.plans.some((p) => p.selfServe && p.monthlyPrice === null)

  return (
    <section
      id="planos"
      style={{
        background: lightPalette.surface,
        borderTop: `1px solid ${lightPalette.borderSoft}`,
      }}
    >
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '88px 24px' }}>
        <div
          data-reveal=""
          style={{
            display: 'flex',
            alignItems: 'baseline',
            gap: 12,
            flexWrap: 'wrap',
            marginBottom: 10,
          }}
        >
          <h2
            style={{
              margin: 0,
              fontSize: 'clamp(26px,3vw,38px)',
              lineHeight: 1.12,
              fontWeight: 600,
              letterSpacing: '-0.035em',
            }}
          >
            {t('landing.pricing.title')}
          </h2>
          {provisional ? (
            <span
              data-testid="pricing-provisional"
              style={{
                fontSize: 11,
                fontWeight: 700,
                padding: '3px 9px',
                borderRadius: 6,
                border: `1px solid ${lightPalette.borderStrong}`,
                color: lightPalette.textFaint,
              }}
            >
              {t('landing.pricing.badge')}
            </span>
          ) : null}
        </div>
        <p
          data-reveal=""
          style={{ margin: '0 0 36px', fontSize: 16, color: lightPalette.textBody }}
        >
          {provisional ? t('landing.pricing.subtitle') : t('landing.pricing.subtitlePriced')}
        </p>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit,minmax(270px,1fr))',
            gap: 18,
            alignItems: 'start',
          }}
        >
          <div
            data-reveal=""
            style={{
              border: `1px solid ${lightPalette.borderDefault}`,
              borderRadius: 18,
              padding: 28,
              display: 'flex',
              flexDirection: 'column',
              gap: 16,
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: 16.5, fontWeight: 700 }}>
                {t('landing.pricing.starterName')}
              </span>
              <span style={{ fontSize: 13, color: lightPalette.textFaint }}>
                {t('landing.pricing.starterDesc')}
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
              <span style={{ fontSize: 34, fontWeight: 800, color: lightPalette.textSubtle }}>
                {priceFor('starter')}
              </span>
              <span style={{ fontSize: 13, color: lightPalette.textSubtle }}>
                {t('landing.pricing.pricePeriod')}
              </span>
            </div>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 9,
                fontSize: 14,
                color: lightPalette.textBody,
              }}
            >
              <span>{t('landing.pricing.starterFeature1')}</span>
              <span>{t('landing.pricing.starterFeature2')}</span>
              <span>{t('landing.pricing.starterFeature3')}</span>
              <span>{t('landing.pricing.starterFeature4')}</span>
            </div>
            <Link
              to="/signup"
              style={{
                marginTop: 4,
                textAlign: 'center',
                fontSize: 14.5,
                fontWeight: 600,
                padding: 11,
                borderRadius: 100,
                border: `1px solid ${lightPalette.borderStrong}`,
                color: lightPalette.ink,
              }}
            >
              {t('landing.pricing.starterCta')}
            </Link>
          </div>

          <div
            data-reveal=""
            style={{
              position: 'relative',
              border: `1.5px solid ${lightPalette.brandAccent}`,
              borderRadius: 18,
              padding: 28,
              display: 'flex',
              flexDirection: 'column',
              gap: 16,
              boxShadow: '0 20px 50px oklch(60% 0.13 233 / 0.14)',
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 16.5, fontWeight: 700 }}>
                  {t('landing.pricing.professionalName')}
                </span>
                <span
                  style={{
                    fontSize: 10.5,
                    fontWeight: 700,
                    padding: '3px 8px',
                    borderRadius: 6,
                    background: lightPalette.brandAccent,
                    color: lightPalette.surface,
                  }}
                >
                  {t('landing.pricing.professionalBadge')}
                </span>
              </div>
              <span style={{ fontSize: 13, color: lightPalette.textFaint }}>
                {t('landing.pricing.professionalDesc')}
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
              <span style={{ fontSize: 34, fontWeight: 800, color: lightPalette.textSubtle }}>
                {priceFor('professional')}
              </span>
              <span style={{ fontSize: 13, color: lightPalette.textSubtle }}>
                {t('landing.pricing.pricePeriod')}
              </span>
            </div>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 9,
                fontSize: 14,
                color: lightPalette.textBody,
              }}
            >
              <span>{t('landing.pricing.professionalFeature1')}</span>
              <span>{t('landing.pricing.professionalFeature2')}</span>
              <span>{t('landing.pricing.professionalFeature3')}</span>
              <span>{t('landing.pricing.professionalFeature4')}</span>
            </div>
            <Link
              to="/signup"
              style={{
                marginTop: 4,
                textAlign: 'center',
                fontSize: 14.5,
                fontWeight: 600,
                padding: 12,
                borderRadius: 100,
                background: lightPalette.ink,
                color: '#fff',
              }}
            >
              {t('landing.pricing.professionalCta')}
            </Link>
          </div>

          <div
            data-reveal=""
            style={{
              border: `1px solid ${lightPalette.borderDefault}`,
              borderRadius: 18,
              padding: 28,
              display: 'flex',
              flexDirection: 'column',
              gap: 16,
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: 16.5, fontWeight: 700 }}>
                {t('landing.pricing.enterpriseName')}
              </span>
              <span style={{ fontSize: 13, color: lightPalette.textFaint }}>
                {t('landing.pricing.enterpriseDesc')}
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
              <span style={{ fontSize: 25, fontWeight: 800 }}>
                {t('landing.pricing.enterprisePrice')}
              </span>
            </div>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 9,
                fontSize: 14,
                color: lightPalette.textBody,
              }}
            >
              <span>{t('landing.pricing.enterpriseFeature1')}</span>
              <span>{t('landing.pricing.enterpriseFeature2')}</span>
              <span>{t('landing.pricing.enterpriseFeature3')}</span>
              <span>{t('landing.pricing.enterpriseFeature4')}</span>
            </div>
            <Link
              to="/signup"
              style={{
                marginTop: 4,
                textAlign: 'center',
                fontSize: 14.5,
                fontWeight: 600,
                padding: 11,
                borderRadius: 100,
                border: `1px solid ${lightPalette.borderStrong}`,
                color: lightPalette.ink,
              }}
            >
              {t('landing.pricing.enterpriseCta')}
            </Link>
          </div>
        </div>
      </div>
    </section>
  )
}
